import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { RESET_PASSWORD } from '@/constants/testIds';
import { MapPin, ArrowLeft, KeyRound, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const missingToken = !token;

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirm) {
            setError('The two passwords don\u2019t match.');
            return;
        }
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, new_password: password });
            toast.success('Password updated. Please sign in with your new password.');
            navigate('/login', { replace: true });
        } catch (err) {
            setError(err?.response?.data?.detail || 'Could not reset your password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--ns-bg)] flex" data-testid={RESET_PASSWORD.page}>
            <div className="hidden lg:flex flex-1 relative overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/3822155/pexels-photo-3822155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                    alt="Travel"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(29,45,68,0.55), rgba(224,122,95,0.35))' }} />
                <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
                    <div className="flex items-center gap-2">
                        <span className="w-10 h-10 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur">
                            <MapPin size={18} />
                        </span>
                        <span className="font-display text-2xl font-semibold">NextStop</span>
                    </div>
                    <div className="max-w-md">
                        <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight tracking-tight">Set a new password</h1>
                        <p className="mt-4 text-white/80 text-lg leading-relaxed">
                            Pick something memorable but strong. At least 8 characters — the longer, the better.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'var(--ns-navy)' }}>
                            <MapPin size={18} className="text-white" />
                        </span>
                        <span className="font-display text-2xl font-semibold">NextStop</span>
                    </div>

                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1.5 text-sm text-[var(--ns-text2)] hover:text-[var(--ns-text)] mb-6"
                    >
                        <ArrowLeft size={14} /> Back to sign in
                    </Link>

                    <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Choose a new password</h2>
                    <p className="text-[var(--ns-text2)] mt-2">Set your new password. You&rsquo;ll sign in with it after saving.</p>

                    {missingToken ? (
                        <div
                            data-testid={RESET_PASSWORD.invalidTokenMessage}
                            className="mt-8 rounded-2xl border border-[var(--ns-clay)]/30 bg-[var(--ns-clay)]/8 p-6"
                        >
                            <div className="flex items-start gap-3">
                                <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(208,92,69,0.14)' }}>
                                    <AlertTriangle size={16} style={{ color: '#D05C45' }} />
                                </span>
                                <div>
                                    <div className="font-display text-lg font-semibold tracking-tight">Missing reset token</div>
                                    <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">
                                        Open this page from the &ldquo;Reset password&rdquo; button in the email we sent.
                                        If the link is old, request a fresh one below.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-5">
                                <Link to="/forgot-password">
                                    <Button className="rounded-full h-10 px-4 text-white" style={{ background: 'var(--ns-navy)' }}>Request new link</Button>
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-8 space-y-4">
                            <div>
                                <Label htmlFor="new-password" className="text-sm text-[var(--ns-text2)]">New password</Label>
                                <div className="relative mt-1">
                                    <KeyRound size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ns-text3)]" />
                                    <Input
                                        id="new-password"
                                        type="password"
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        data-testid={RESET_PASSWORD.passwordInput}
                                        className="h-12 rounded-xl border-[var(--ns-border)] bg-white pl-10"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="confirm-password" className="text-sm text-[var(--ns-text2)]">Confirm password</Label>
                                <div className="relative mt-1">
                                    <KeyRound size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ns-text3)]" />
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        required
                                        minLength={8}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        placeholder="Type it again"
                                        data-testid={RESET_PASSWORD.confirmInput}
                                        className="h-12 rounded-xl border-[var(--ns-border)] bg-white pl-10"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div
                                    data-testid={RESET_PASSWORD.errorAlert}
                                    className="text-sm text-[var(--ns-clayHover)] bg-[var(--ns-clay)]/10 border border-[var(--ns-clay)]/30 rounded-xl px-4 py-3"
                                >
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid={RESET_PASSWORD.submitButton}
                                className="w-full h-12 rounded-full font-medium text-white"
                                style={{ background: 'var(--ns-navy)' }}
                            >
                                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Updating…</> : 'Update password'}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
